import { useState, useEffect, useCallback, useRef } from 'react';
import { Activity, ActivityApi } from '../../../Model/Activity/Activity';

const PAGE_SIZE = 20;

export type UseActivitySearchOptions = {
  language: string;
  enabled: boolean;
};

export type UseActivitySearchResult = {
  activities: Activity[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  query: string;
  setQuery: (query: string) => void;
  loadMore: () => void;
  reset: () => void;
};

export function useActivitySearch({ language, enabled }: UseActivitySearchOptions): UseActivitySearchResult {
  const [query, setQuery] = useState('');
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);

  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const fetchActivities = useCallback(
    async (searchQuery: string, pageNum: number, append: boolean = false) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setActivities([]);
      }

      try {
        const results = await ActivityApi.find(
          {
            query: searchQuery || undefined,
            sort: `nameMap.${language},ASC`,
            page: pageNum,
            size: PAGE_SIZE,
          },
          abortControllerRef.current.signal,
        );

        setActivities((prev) => (append ? [...prev, ...results] : results));
        setHasMore(results.length === PAGE_SIZE);
        setPage(pageNum);
      } catch (err: any) {
        if (err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') {
          console.error('Error fetching activities:', err);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [language],
  );

  const reset = useCallback(() => {
    setQuery('');
    setActivities([]);
    setPage(0);
    setHasMore(true);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      reset();
      return;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(
      () => {
        fetchActivities(query, 0);
      },
      query ? 300 : 0,
    );

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [query, enabled, fetchActivities, reset]);

  const loadMore = useCallback(() => {
    if (!loading && !loadingMore && hasMore) {
      fetchActivities(query, page + 1, true);
    }
  }, [loading, loadingMore, hasMore, query, page, fetchActivities]);

  return {
    activities,
    loading,
    loadingMore,
    hasMore,
    query,
    setQuery,
    loadMore,
    reset,
  };
}
